import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../../../components/ui/PageHeader';
import Skeleton from '../../../components/ui/Skeleton';
import PathwayCard from '../../../components/dailyAudit/PathwayCard';
import { useShop } from '../../../hooks/useShop';
import { api } from '../../../api/client';
import { ArrowLeft } from 'lucide-react';

export default function DailyAuditDatePage() {
  const { date } = useParams();
  const { currentShop, currentShopId } = useShop();
  const noShop = !currentShopId;
  const [cod, setCod] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentShopId || noShop || !date) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.dailyAuditPathwaySummary(date, 'cod_checks', currentShopId),
      api.dailyAuditPathwaySummary(date, 'statement_vendors', currentShopId),
    ])
      .then(([c, s]) => { if (!cancelled) { setCod(c); setStmt(s); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [date, currentShopId, noShop]);

  const dateObj = new Date(date + 'T00:00:00');
  const human = dateObj.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const shopName = currentShop?.shop_nickname || currentShop?.shop_name || '';

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Daily Audit', href: '/auditor/daily-audit' },
          { label: human },
        ]}
        title={human}
        description={`Audit pathways for ${shopName} on this date.`}
        actions={
          <Link
            to="/auditor/daily-audit"
            className="text-[13px] text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)] flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to date list
          </Link>
        }
      />

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-[var(--color-danger-300)] bg-[var(--color-danger-50)] p-4 text-[var(--color-danger-700)] text-[14px]">
          Failed to load: {error}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-3">
          <PathwayCard
            pathway="statement_vendors"
            summary={stmt}
            basePath={`/auditor/daily-audit/${date}`}
            disabled
          />
          <PathwayCard
            pathway="cod_checks"
            summary={cod}
            basePath={`/auditor/daily-audit/${date}`}
          />
        </div>
      )}
    </div>
  );
}
